import type { Directive, DirectiveBinding } from 'vue'

interface TooltipState {
  text: string
  position: 'top' | 'bottom' | 'left' | 'right'
  tooltip: HTMLElement | null
  show: () => void
  hide: () => void
  focusin: () => void
  keydown: (event: KeyboardEvent) => void
}

const states = new WeakMap<HTMLElement, TooltipState>()
let nextId = 0

function positionTooltip(
  element: HTMLElement,
  tooltip: HTMLElement,
  position: TooltipState['position'],
) {
  const target = element.getBoundingClientRect()
  const popup = tooltip.getBoundingClientRect()
  const gap = 8
  let left = target.left + (target.width - popup.width) / 2
  let top = target.top - popup.height - gap

  if (position === 'bottom') top = target.bottom + gap
  if (position === 'left') {
    left = target.left - popup.width - gap
    top = target.top + (target.height - popup.height) / 2
  }
  if (position === 'right') {
    left = target.right + gap
    top = target.top + (target.height - popup.height) / 2
  }

  tooltip.style.left = `${Math.max(8, Math.min(left, window.innerWidth - popup.width - 8))}px`
  tooltip.style.top = `${Math.max(8, Math.min(top, window.innerHeight - popup.height - 8))}px`
}

function resolvePosition(binding: DirectiveBinding<string | undefined>): TooltipState['position'] {
  if (binding.modifiers.bottom) return 'bottom'
  if (binding.modifiers.left) return 'left'
  if (binding.modifiers.right) return 'right'
  return 'top'
}

export const appTooltip: Directive<HTMLElement, string | undefined> = {
  mounted(element, binding) {
    const state: TooltipState = {
      text: binding.value ?? '',
      position: resolvePosition(binding),
      tooltip: null,
      show: () => {
        if (!state.text || state.tooltip) return
        const tooltip = document.createElement('div')
        tooltip.id = `app-tooltip-${++nextId}`
        tooltip.role = 'tooltip'
        tooltip.textContent = state.text
        tooltip.className =
          'pointer-events-none fixed z-[10000] max-w-72 rounded-md bg-inverted px-2 py-1 text-xs text-inverted shadow-lg'
        document.body.append(tooltip)
        state.tooltip = tooltip
        element.setAttribute('aria-describedby', tooltip.id)
        positionTooltip(element, tooltip, state.position)
      },
      hide: () => {
        state.tooltip?.remove()
        state.tooltip = null
        element.removeAttribute('aria-describedby')
      },
      // Keyboard focus only: a popover moving focus onto this element after a tap or click
      // would otherwise pop the tooltip over whatever the user just opened.
      focusin: () => {
        if (element.matches(':focus-visible')) state.show()
      },
      keydown: (event) => {
        if (event.key === 'Escape') state.hide()
      },
    }

    states.set(element, state)
    element.addEventListener('mouseenter', state.show)
    element.addEventListener('mouseleave', state.hide)
    element.addEventListener('focusin', state.focusin)
    element.addEventListener('focusout', state.hide)
    element.addEventListener('keydown', state.keydown)
  },
  updated(element, binding) {
    const state = states.get(element)
    if (!state) return
    state.text = binding.value ?? ''
    state.position = resolvePosition(binding)
    if (state.tooltip) {
      state.tooltip.textContent = state.text
      positionTooltip(element, state.tooltip, state.position)
    }
  },
  unmounted(element) {
    const state = states.get(element)
    if (!state) return
    state.hide()
    element.removeEventListener('mouseenter', state.show)
    element.removeEventListener('mouseleave', state.hide)
    element.removeEventListener('focusin', state.focusin)
    element.removeEventListener('focusout', state.hide)
    element.removeEventListener('keydown', state.keydown)
    states.delete(element)
  },
}
